// ./src/utils/carHelpers.ts
// Helper functions for car-related calculations and display
// All constants are imported from ./src/config/

import { Car, CarTotalSummary, CarMonthlySummary } from "@/utils/api";
import {
  VehicleType,
  getVehicleTypeFromBodyTypeId,
} from "@/config/bodyTypes";
import {
  ELECTRIC_FUEL_TYPES,
  LIQUID_FUEL_TYPES,
  GAS_FUEL_TYPES,
  getFuelTypeOption,
} from "@/config/fuelTypes";
import {
  getEfficiencyStatus as getEfficiencyStatusFromThresholds,
  getEfficiencyStatusColor,
  EfficiencyStatus,
} from "@/config/efficiencyThresholds";
import { getDriveTypeOption } from "@/config/driveTypes";
import { getTankVolumeUnitOption, getConsumptionUnitOption } from "@/config/unitOptions";
import { volumeUnitToLiters } from "@/utils/formatHelpers";

// ============================================
// Re-exports for convenience
// ============================================

export type { VehicleType, EfficiencyStatus };
export { getVehicleTypeFromBodyTypeId, getEfficiencyStatusColor };

// ============================================
// Types
// ============================================

type MaybeSummary<T> = T | T[] | null | undefined;

// ============================================
// Internal Helpers
// ============================================

/**
 * Normalizes a summary input to always be an array
 */
const normalizeSummaryArray = <T>(summary: MaybeSummary<T>): T[] => {
  if (!summary) return [];
  return Array.isArray(summary) ? summary : [summary];
};

// ============================================
// Vehicle Type Detection
// ============================================

/**
 * Get vehicle type from car object
 * @param car - Car object
 * @param bodyTypeId - Optional body type ID (if not on car object)
 * @returns Vehicle type category
 */
export const getVehicleType = (
  car: Car | null | undefined,
  bodyTypeId?: string | number | null
): VehicleType => {
  if (!car) return "car";
  const id = bodyTypeId ?? car.bodyTypeId;
  return getVehicleTypeFromBodyTypeId(id);
};

// ============================================
// Fuel Type Helpers
// ============================================

/**
 * Get the main fuel type for a car
 * Falls back to deprecated typeOfFuel field if mainTankFuelType is not set
 * @param car - Car object
 * @returns Fuel type string or null
 */
export const getFuelType = (car: Car | null | undefined): string | null => {
  if (!car) return null;
  return car.mainTankFuelType || car.typeOfFuel || null;
};

/**
 * Get the additional tank fuel type for a car
 * @param car - Car object
 * @returns Fuel type string or null
 */
export const getAdditionalFuelType = (car: Car | null | undefined): string | null => {
  if (!car) return null;
  return car.addlTankFuelType || null;
};

/**
 * Check if a car uses electric power (fully electric or hydrogen fuel cell)
 * @param car - Car object
 * @returns True if car is electric
 */
export const isElectricVehicle = (car: Car | null | undefined): boolean => {
  const fuelType = getFuelType(car);
  if (!fuelType) return false;
  return ELECTRIC_FUEL_TYPES.has(fuelType);
};

/**
 * Check if a car has a secondary/additional fuel tank
 * @param car - Car object
 * @returns True if car has additional tank configured
 */
export const hasAdditionalTank = (car: Car | null | undefined): boolean => {
  if (!car) return false;
  return Boolean(car.addlTankFuelType && car.addlTankVolume && car.addlTankVolume > 0);
};

/**
 * Check if a car is a hybrid (has both liquid fuel and electric)
 * @param car - Car object
 * @returns True if car is hybrid
 */
export const isHybridVehicle = (car: Car | null | undefined): boolean => {
  if (!car) return false;

  const mainFuel = getFuelType(car);
  const addlFuel = getAdditionalFuelType(car);

  if (!mainFuel || !addlFuel) return false;

  const mainIsElectric = ELECTRIC_FUEL_TYPES.has(mainFuel);
  const addlIsElectric = ELECTRIC_FUEL_TYPES.has(addlFuel);
  const mainIsLiquid = LIQUID_FUEL_TYPES.has(mainFuel);
  const addlIsLiquid = LIQUID_FUEL_TYPES.has(addlFuel);

  return (mainIsElectric && addlIsLiquid) || (mainIsLiquid && addlIsElectric);
};

/**
 * Check if a car is dual-fuel (e.g., gasoline + LPG)
 * @param car - Car object
 * @returns True if car uses two different liquid/gas fuels
 */
export const isDualFuelVehicle = (car: Car | null | undefined): boolean => {
  if (!car) return false;

  const mainFuel = getFuelType(car);
  const addlFuel = getAdditionalFuelType(car);

  if (!mainFuel || !addlFuel) return false;

  const mainIsLiquidOrGas = LIQUID_FUEL_TYPES.has(mainFuel) || GAS_FUEL_TYPES.has(mainFuel);
  const addlIsLiquidOrGas = LIQUID_FUEL_TYPES.has(addlFuel) || GAS_FUEL_TYPES.has(addlFuel);

  return mainIsLiquidOrGas && addlIsLiquidOrGas && mainFuel !== addlFuel;
};

/**
 * Get display name for a fuel type
 * @param fuelType - Fuel type code
 * @param t - Translation function
 * @returns Human-readable fuel type name
 */
export const getFuelTypeDisplayName = (
  fuelType: string | null | undefined,
  t?: (key: string) => string
): string => {
  if (!fuelType) return "";

  const option = getFuelTypeOption(fuelType);
  if (option && t) {
    return t(`common:${option.labelKey}`);
  }

  // Fallback: capitalize the value
  return fuelType.charAt(0).toUpperCase() + fuelType.slice(1);
};

/**
 * Get the consumption unit key for a car (matches CONSUMPTION_UNIT_OPTIONS values)
 * @param car - Car object
 * @returns Consumption unit key (e.g., "l100km", "kwh100km")
 */
export const getConsumptionUnitKey = (car: Car | null | undefined): string => {
  if (isElectricVehicle(car)) {
    return "kwh100km";
  }
  return "l100km";
};

/**
 * Get the consumption unit display string for a car
 * @param car - Car object
 * @param t - Optional translation function
 * @returns Consumption unit display string (e.g., "L/100km", "kWh/100km")
 */
export const getConsumptionUnitDisplay = (
  car: Car | null | undefined,
  t?: (key: string) => string
): string => {
  const unitKey = getConsumptionUnitKey(car);
  const option = getConsumptionUnitOption(unitKey);

  if (option && t) {
    return t(`common:${option.labelShortKey}`);
  }

  // Fallback display strings
  if (isElectricVehicle(car)) {
    return "kWh/100km";
  }
  return "L/100km";
};

// ============================================
// Tank Volume Helpers
// ============================================

/**
 * Get the main tank volume in the entered unit
 * Falls back to deprecated tankVolume field if mainTankVolume is not set
 * @param car - Car object
 * @returns Tank volume or null
 */
export const getMainTankVolume = (car: Car | null | undefined): number | null => {
  if (!car) return null;
  return car.mainTankVolume ?? car.tankVolume ?? null;
};

/**
 * Get the main tank volume unit
 * @param car - Car object
 * @returns Volume unit or default 'l' (liters)
 */
export const getMainTankVolumeUnit = (car: Car | null | undefined): string => {
  if (!car || !car.mainTankVolumeEnteredIn) {
    if (isElectricVehicle(car)) {
      return "kwh";
    }
    return "l";
  }
  return car.mainTankVolumeEnteredIn;
};

/**
 * Get the additional tank volume in the entered unit
 * Falls back to deprecated additionalTankVolume field if addlTankVolume is not set
 * @param car - Car object
 * @returns Tank volume or null
 */
export const getAdditionalTankVolume = (car: Car | null | undefined): number | null => {
  if (!car) return null;
  return car.addlTankVolume ?? car.additionalTankVolume ?? null;
};

/**
 * Get the additional tank volume unit
 * @param car - Car object
 * @returns Volume unit or default 'l' (liters)
 */
export const getAdditionalTankVolumeUnit = (car: Car | null | undefined): string => {
  if (!car || !car.addlTankVolumeEnteredIn) {
    const addlFuel = getAdditionalFuelType(car);
    if (addlFuel && ELECTRIC_FUEL_TYPES.has(addlFuel)) {
      return "kwh";
    }
    return "l";
  }
  return car.addlTankVolumeEnteredIn;
};

/**
 * Get total tank capacity in liters (main tank only, for liquid fuels)
 * @param car - Car object
 * @returns Total capacity in liters or null
 */
export const getTotalTankCapacityLiters = (car: Car | null | undefined): number | null => {
  if (!car) return null;
  if (isElectricVehicle(car)) return null;

  const mainVolume = getMainTankVolume(car);
  const mainUnit = getMainTankVolumeUnit(car);

  // kWh is energy, not volume - can't convert to liters
  if (mainUnit === "kwh") return null;
  if (mainVolume === null) return null;

  return volumeUnitToLiters(mainVolume, mainUnit);
};

/**
 * Get battery capacity in kWh (for electric vehicles)
 * @param car - Car object
 * @returns Battery capacity in kWh or null
 */
export const getBatteryCapacityKwh = (car: Car | null | undefined): number | null => {
  if (!car) return null;

  // Check main tank for electric
  if (isElectricVehicle(car)) {
    const mainVolume = getMainTankVolume(car);
    const mainUnit = getMainTankVolumeUnit(car);
    if (mainUnit === "kwh") {
      return mainVolume;
    }
  }

  // Check additional tank for hybrid with electric secondary
  const addlFuel = getAdditionalFuelType(car);
  if (addlFuel && ELECTRIC_FUEL_TYPES.has(addlFuel)) {
    const addlVolume = getAdditionalTankVolume(car);
    const addlUnit = getAdditionalTankVolumeUnit(car);
    if (addlUnit === "kwh") {
      return addlVolume;
    }
  }

  return null;
};

// ============================================
// Odometer Helpers
// ============================================

/**
 * Get the latest/best available odometer reading for a car
 * @param car - Car object
 * @param totalSummary - Optional total summary (single or array)
 * @param monthlySummary - Optional monthly summary (single or array)
 * @returns Best available odometer reading in km
 */
export const getLatestOdometer = (
  car: Car | null | undefined,
  totalSummary?: MaybeSummary<CarTotalSummary>,
  monthlySummary?: MaybeSummary<CarMonthlySummary>
): number => {
  const monthlySummaries = normalizeSummaryArray(monthlySummary);
  const totalSummaries = normalizeSummaryArray(totalSummary);

  // Priority 1: Find max endMileage from monthly summaries
  if (monthlySummaries.length > 0) {
    const maxMonthlyMileage = Math.max(
      ...monthlySummaries.map((s) => s.endMileage || 0)
    );
    if (maxMonthlyMileage > 0) {
      return maxMonthlyMileage;
    }
  }

  // Priority 2: Find max latestKnownMileage from total summaries
  if (totalSummaries.length > 0) {
    const maxTotalMileage = Math.max(
      ...totalSummaries.map((s) => s.latestKnownMileage || 0)
    );
    if (maxTotalMileage > 0) {
      return maxTotalMileage;
    }
  }

  // Priority 3: Fall back to car's current mileage if available
  if ((car as any)?.currentMileage && (car as any).currentMileage > 0) {
    return (car as any).currentMileage;
  }

  // Priority 4: Fall back to car's initial mileage
  if (car?.initialMileage && car.initialMileage > 0) {
    return car.initialMileage;
  }

  return 0;
};

/**
 * Calculate monthly mileage change from monthly summary
 * @param monthlySummary - Monthly summary object (single or array)
 * @returns Kilometers driven this month (or 0 if not calculable)
 */
export const getMonthlyMileageChange = (
  monthlySummary: MaybeSummary<CarMonthlySummary>
): number => {
  const summaries = normalizeSummaryArray(monthlySummary);

  if (summaries.length === 0) return 0;

  let minStart = Infinity;
  let maxEnd = 0;

  for (const summary of summaries) {
    const start = summary.startMileage || 0;
    const end = summary.endMileage || 0;

    if (start > 0 && start < minStart) {
      minStart = start;
    }
    if (end > maxEnd) {
      maxEnd = end;
    }
  }

  if (minStart === Infinity) {
    minStart = 0;
  }

  return Math.max(0, maxEnd - minStart);
};

/**
 * Get total mileage from total summary
 * @param totalSummary - Total summary object (single or array)
 * @returns Latest known mileage (or 0 if not available)
 */
export const getTotalMileage = (
  totalSummary: MaybeSummary<CarTotalSummary>
): number => {
  const summaries = normalizeSummaryArray(totalSummary);

  if (summaries.length === 0) return 0;

  return Math.max(...summaries.map((s) => s.latestKnownMileage || 0));
};

/**
 * Get the earliest known mileage from monthly summaries
 * @param monthlySummary - Monthly summary object (single or array)
 * @returns Earliest known mileage (or 0 if not available)
 */
export const getEarliestMileage = (
  monthlySummary: MaybeSummary<CarMonthlySummary>
): number => {
  const summaries = normalizeSummaryArray(monthlySummary);

  if (summaries.length === 0) return 0;

  let earliestYear = Infinity;
  let earliestMonth = Infinity;

  for (const summary of summaries) {
    const year = summary.year || 0;
    const month = summary.month || 0;

    if (year < earliestYear || (year === earliestYear && month < earliestMonth)) {
      earliestYear = year;
      earliestMonth = month;
    }
  }

  if (earliestYear === Infinity) return 0;

  const earliestMonthSummaries = summaries.filter(
    (s) => s.year === earliestYear && s.month === earliestMonth
  );

  const validMileages = earliestMonthSummaries
    .map((s) => s.startMileage || 0)
    .filter((m) => m > 0);

  if (validMileages.length === 0) return 0;

  return Math.min(...validMileages);
};

/**
 * Calculate total distance traveled from monthly summaries
 * @param monthlySummary - Monthly summary object (single or array)
 * @returns Total distance traveled (or 0 if not calculable)
 */
export const getTotalDistanceTraveled = (
  monthlySummary: MaybeSummary<CarMonthlySummary>
): number => {
  const summaries = normalizeSummaryArray(monthlySummary);

  if (summaries.length === 0) return 0;

  const earliest = getEarliestMileage(summaries);
  const latest = Math.max(...summaries.map((s) => s.endMileage || 0));

  if (earliest === 0 || latest === 0) {
    return 0;
  }

  return Math.max(0, latest - earliest);
};

// ============================================
// Travel Distance Helpers
// ============================================

/**
 * Get monthly travel distance from monthly summary
 * @param monthlySummary - Monthly summary object (single or array)
 * @returns Total travel distance this month (or 0)
 */
export const getMonthlyTravelDistance = (
  monthlySummary: MaybeSummary<CarMonthlySummary>
): number => {
  const summaries = normalizeSummaryArray(monthlySummary);

  if (summaries.length === 0) return 0;

  return summaries.reduce((total, s) => total + (s.travelsDistance || 0), 0);
};

/**
 * Get total travel distance from total summary
 * @param totalSummary - Total summary object (single or array)
 * @returns Total travel distance (or 0)
 */
export const getTotalTravelDistance = (
  totalSummary: MaybeSummary<CarTotalSummary>
): number => {
  const summaries = normalizeSummaryArray(totalSummary);

  if (summaries.length === 0) return 0;

  return summaries.reduce((total, s) => total + (s.totalTravelsDistance || 0), 0);
};

/**
 * Get monthly travel count from monthly summary
 * @param monthlySummary - Monthly summary object (single or array)
 * @returns Number of travels this month (or 0)
 */
export const getMonthlyTravelCount = (
  monthlySummary: MaybeSummary<CarMonthlySummary>
): number => {
  const summaries = normalizeSummaryArray(monthlySummary);

  if (summaries.length === 0) return 0;

  return summaries.reduce((total, s) => total + (s.travelsCount || 0), 0);
};

/**
 * Get total travel count from total summary
 * @param totalSummary - Total summary object (single or array)
 * @returns Total number of travels (or 0)
 */
export const getTotalTravelCount = (
  totalSummary: MaybeSummary<CarTotalSummary>
): number => {
  const summaries = normalizeSummaryArray(totalSummary);

  if (summaries.length === 0) return 0;

  return summaries.reduce((total, s) => total + (s.totalTravelsCount || 0), 0);
};

// ============================================
// Efficiency Calculations
// ============================================

/**
 * Calculate fuel/energy efficiency from volume and distance
 * @param totalVolume - Fuel volume in liters or energy in kWh
 * @param totalDistance - Distance traveled in kilometers
 * @returns Efficiency in L/100km or kWh/100km (or null if not calculable)
 */
export const calculateEfficiency = (
  totalVolume: number | null | undefined,
  totalDistance: number | null | undefined
): number | null => {
  if (
    !totalVolume ||
    !totalDistance ||
    totalVolume <= 0 ||
    totalDistance <= 0
  ) {
    return null;
  }

  return (totalVolume / totalDistance) * 100;
};

/**
 * Calculate efficiency from monthly summary using proper consumption tracking
 * @param monthlySummary - Monthly summary object (single or array)
 * @returns Efficiency in L/100km or kWh/100km (or null if not calculable)
 */
export const getMonthlyEfficiency = (
  monthlySummary: MaybeSummary<CarMonthlySummary>
): number | null => {
  const summaries = normalizeSummaryArray(monthlySummary);

  if (summaries.length === 0) return null;

  let totalConsumptionVolume = 0;
  let totalDistance = 0;
  let hasValidData = false;

  for (const summary of summaries) {
    if (summary.isFirstRefuelMonth) {
      continue;
    }

    const consumptionVolume = summary.consumptionVolume || 0;
    const distance = (summary.endMileage || 0) - (summary.startMileage || 0);

    if (consumptionVolume > 0 && distance > 0) {
      totalConsumptionVolume += consumptionVolume;
      totalDistance += distance;
      hasValidData = true;
    }
  }

  if (!hasValidData) {
    return null;
  }

  return calculateEfficiency(totalConsumptionVolume, totalDistance);
};

/**
 * Calculate efficiency from total summary using proper consumption tracking
 * @param totalSummary - Total summary object (single or array)
 * @returns Efficiency in L/100km or kWh/100km (or null if not calculable)
 */
export const getTotalEfficiency = (
  totalSummary: MaybeSummary<CarTotalSummary>
): number | null => {
  const summaries = normalizeSummaryArray(totalSummary);

  if (summaries.length === 0) return null;

  let totalConsumptionVolume = 0;
  let totalConsumptionDistance = 0;

  for (const summary of summaries) {
    const consumptionVolume = summary.consumptionVolume || 0;
    const consumptionDistance = summary.consumptionDistance || 0;

    if (consumptionVolume > 0 && consumptionDistance > 0) {
      totalConsumptionVolume += consumptionVolume;
      totalConsumptionDistance += consumptionDistance;
    }
  }

  return calculateEfficiency(totalConsumptionVolume, totalConsumptionDistance);
};

/**
 * Get efficiency status based on consumption value and vehicle type
 * @param efficiency - Efficiency value (L/100km or kWh/100km)
 * @param vehicleType - Type of vehicle
 * @param isElectric - Whether vehicle is electric (uses kWh/100km thresholds)
 * @returns Efficiency status
 */
export const getEfficiencyStatus = (
  efficiency: number | null | undefined,
  vehicleType: VehicleType = "car",
  isElectric: boolean = false
): EfficiencyStatus | null => {
  return getEfficiencyStatusFromThresholds(efficiency, vehicleType, isElectric);
};

/**
 * Get efficiency status for a car based on its fuel type
 * @param efficiency - Efficiency value
 * @param car - Car object
 * @returns Efficiency status
 */
export const getCarEfficiencyStatus = (
  efficiency: number | null | undefined,
  car: Car | null | undefined
): EfficiencyStatus | null => {
  const vehicleType = getVehicleType(car);
  const isElectric = isElectricVehicle(car);
  return getEfficiencyStatus(efficiency, vehicleType, isElectric);
};

// ============================================
// Cost Calculations
// ============================================

/**
 * Calculate total monthly costs from monthly summary
 * Includes expenses (with maintenance), fees, taxes, and refuel costs
 * @param monthlySummary - Monthly summary object
 * @returns Total cost in home currency
 */
export const getMonthlyTotalCost = (
  monthlySummary: CarMonthlySummary | null | undefined
): number => {
  if (!monthlySummary) return 0;

  return (
    (monthlySummary.expensesCost || 0) +
    (monthlySummary.expensesFees || 0) +
    (monthlySummary.expensesTaxes || 0) +
    (monthlySummary.refuelsCost || 0) +
    (monthlySummary.refuelsTaxes || 0)
  );
};

/**
 * Calculate total all-time costs from total summary
 * @param totalSummary - Total summary object
 * @returns Total cost in home currency
 */
export const getTotalCost = (
  totalSummary: CarTotalSummary | null | undefined
): number => {
  if (!totalSummary) return 0;

  return (
    (totalSummary.totalExpensesCost || 0) +
    (totalSummary.expensesFees || 0) +
    (totalSummary.expensesTaxes || 0) +
    (totalSummary.totalRefuelsCost || 0) +
    (totalSummary.refuelsTaxes || 0)
  );
};

/**
 * Get monthly refuel costs from monthly summary
 * @param monthlySummary - Monthly summary object
 * @returns Refuel cost in home currency
 */
export const getMonthlyRefuelCost = (
  monthlySummary: CarMonthlySummary | null | undefined
): number => {
  if (!monthlySummary) return 0;

  return (
    (monthlySummary.refuelsCost || 0) +
    (monthlySummary.refuelsTaxes || 0)
  );
};

/**
 * Get total refuel costs from total summary
 * @param totalSummary - Total summary object
 * @returns Total refuel cost in home currency
 */
export const getTotalRefuelCost = (
  totalSummary: CarTotalSummary | null | undefined
): number => {
  if (!totalSummary) return 0;

  return (
    (totalSummary.totalRefuelsCost || 0) +
    (totalSummary.refuelsTaxes || 0)
  );
};

/**
 * Get monthly expense costs (excluding refuels) from monthly summary
 * @param monthlySummary - Monthly summary object
 * @returns Expense cost in home currency
 */
export const getMonthlyExpenseCost = (
  monthlySummary: CarMonthlySummary | null | undefined
): number => {
  if (!monthlySummary) return 0;

  return (
    (monthlySummary.expensesCost || 0) +
    (monthlySummary.expensesFees || 0) +
    (monthlySummary.expensesTaxes || 0)
  );
};

/**
 * Get total expense costs (excluding refuels) from total summary
 * @param totalSummary - Total summary object
 * @returns Total expense cost in home currency
 */
export const getTotalExpenseCost = (
  totalSummary: CarTotalSummary | null | undefined
): number => {
  if (!totalSummary) return 0;

  return (
    (totalSummary.totalExpensesCost || 0) +
    (totalSummary.expensesFees || 0) +
    (totalSummary.expensesTaxes || 0)
  );
};

// ============================================
// Maintenance Cost Calculations
// ============================================

/**
 * Get monthly maintenance costs from monthly summary
 * @param monthlySummary - Monthly summary object
 * @returns Maintenance cost in home currency
 */
export const getMonthlyMaintenanceCost = (
  monthlySummary: CarMonthlySummary | null | undefined
): number => {
  if (!monthlySummary) return 0;
  return monthlySummary.maintenanceCost || 0;
};

/**
 * Get total maintenance costs from total summary
 * @param totalSummary - Total summary object
 * @returns Total maintenance cost in home currency
 */
export const getTotalMaintenanceCost = (
  totalSummary: CarTotalSummary | null | undefined
): number => {
  if (!totalSummary) return 0;
  return totalSummary.totalMaintenanceCost || 0;
};

/**
 * Get monthly maintenance count from monthly summary
 * @param monthlySummary - Monthly summary object
 * @returns Number of maintenance records this month
 */
export const getMonthlyMaintenanceCount = (
  monthlySummary: CarMonthlySummary | null | undefined
): number => {
  if (!monthlySummary) return 0;
  return monthlySummary.maintenanceCount || 0;
};

/**
 * Get total maintenance count from total summary
 * @param totalSummary - Total summary object
 * @returns Total number of maintenance records
 */
export const getTotalMaintenanceCount = (
  totalSummary: CarTotalSummary | null | undefined
): number => {
  if (!totalSummary) return 0;
  return totalSummary.totalMaintenanceCount || 0;
};

// ============================================
// Revenue Calculations
// ============================================

/**
 * Get monthly revenue from monthly summary
 * @param monthlySummary - Monthly summary object
 * @returns Revenue amount in home currency
 */
export const getMonthlyRevenue = (
  monthlySummary: CarMonthlySummary | null | undefined
): number => {
  if (!monthlySummary) return 0;
  return monthlySummary.revenuesAmount || 0;
};

/**
 * Get total revenue from total summary
 * @param totalSummary - Total summary object
 * @returns Total revenue amount in home currency
 */
export const getTotalRevenue = (
  totalSummary: CarTotalSummary | null | undefined
): number => {
  if (!totalSummary) return 0;
  return totalSummary.totalRevenuesAmount || 0;
};

/**
 * Get monthly revenue count from monthly summary
 * @param monthlySummary - Monthly summary object
 * @returns Number of revenue records this month
 */
export const getMonthlyRevenueCount = (
  monthlySummary: CarMonthlySummary | null | undefined
): number => {
  if (!monthlySummary) return 0;
  return monthlySummary.revenuesCount || 0;
};

/**
 * Get total revenue count from total summary
 * @param totalSummary - Total summary object
 * @returns Total number of revenue records
 */
export const getTotalRevenueCount = (
  totalSummary: CarTotalSummary | null | undefined
): number => {
  if (!totalSummary) return 0;
  return totalSummary.totalRevenuesCount || 0;
};

// ============================================
// Net Cost Calculations (Cost - Revenue)
// ============================================

/**
 * Calculate monthly net cost (expenses minus revenue)
 * Positive = net expense, Negative = net profit
 * @param monthlySummary - Monthly summary object
 * @returns Net cost in home currency
 */
export const getMonthlyNetCost = (
  monthlySummary: CarMonthlySummary | null | undefined
): number => {
  const cost = getMonthlyTotalCost(monthlySummary);
  const revenue = getMonthlyRevenue(monthlySummary);
  return cost - revenue;
};

/**
 * Calculate total net cost (expenses minus revenue)
 * Positive = net expense, Negative = net profit
 * @param totalSummary - Total summary object
 * @returns Net cost in home currency
 */
export const getTotalNetCost = (
  totalSummary: CarTotalSummary | null | undefined
): number => {
  const cost = getTotalCost(totalSummary);
  const revenue = getTotalRevenue(totalSummary);
  return cost - revenue;
};

// ============================================
// Cost Per Distance Calculations
// ============================================

/**
 * Calculate cost per kilometer from summary data
 * @param totalCost - Total cost in home currency
 * @param totalDistance - Total distance in kilometers
 * @returns Cost per km (or null if not calculable)
 */
export const calculateCostPerKm = (
  totalCost: number | null | undefined,
  totalDistance: number | null | undefined
): number | null => {
  if (!totalCost || !totalDistance || totalDistance <= 0) {
    return null;
  }

  return totalCost / totalDistance;
};

/**
 * Get cost per km from total summary
 * @param totalSummary - Total summary object
 * @returns Cost per km (or null if not calculable)
 */
export const getTotalCostPerKm = (
  totalSummary: CarTotalSummary | null | undefined
): number | null => {
  if (!totalSummary) return null;

  const cost = getTotalCost(totalSummary);
  const distance = getTotalMileage(totalSummary);

  return calculateCostPerKm(cost, distance);
};

/**
 * Get net cost per km from total summary (cost - revenue) / distance
 * @param totalSummary - Total summary object
 * @returns Net cost per km (or null if not calculable)
 */
export const getTotalNetCostPerKm = (
  totalSummary: CarTotalSummary | null | undefined
): number | null => {
  if (!totalSummary) return null;

  const netCost = getTotalNetCost(totalSummary);
  const distance = getTotalMileage(totalSummary);

  if (!distance || distance <= 0) return null;

  return netCost / distance;
};

// ============================================
// Display Helpers
// ============================================

/**
 * Get car display name (label or make + model)
 * @param car - Car object
 * @returns Display name string
 */
export const getCarDisplayName = (car: Car | null | undefined): string => {
  if (!car) return "";

  if (car.label) {
    return car.label;
  }

  const parts: string[] = [];

  if (car.vehicleMake?.makeName) {
    parts.push(car.vehicleMake.makeName);
  }

  if (car.model) {
    parts.push(car.model);
  }

  if (car.manufacturedIn) {
    parts.push(String(car.manufacturedIn));
  }

  return parts.join(" ") || "Unknown Vehicle";
};

/**
 * Get car subtitle (make, model, year)
 * @param car - Car object
 * @returns Subtitle string
 */
export const getCarSubtitle = (car: Car | null | undefined): string => {
  if (!car) return "";

  const parts: string[] = [];

  if (car.vehicleMake?.makeName) {
    parts.push(car.vehicleMake.makeName);
  }

  if (car.model) {
    parts.push(car.model);
  }

  if (car.manufacturedIn) {
    parts.push(String(car.manufacturedIn));
  }

  return parts.join(" • ");
};

/**
 * Get car detail line (license plate, fuel type)
 * @param car - Car object
 * @param t - Optional translation function
 * @returns Detail string
 */
export const getCarDetailLine = (
  car: Car | null | undefined,
  t?: (key: string) => string
): string => {
  if (!car) return "";

  const parts: string[] = [];

  if (car.licensePlate) {
    parts.push(car.licensePlate);
  }

  const fuelType = getFuelType(car);
  if (fuelType) {
    parts.push(getFuelTypeDisplayName(fuelType, t));
  }

  if (isHybridVehicle(car)) {
    const addlFuel = getAdditionalFuelType(car);
    if (addlFuel) {
      parts.push(`+ ${getFuelTypeDisplayName(addlFuel, t)}`);
    }
  } else if (isDualFuelVehicle(car)) {
    const addlFuel = getAdditionalFuelType(car);
    if (addlFuel) {
      parts.push(`/ ${getFuelTypeDisplayName(addlFuel, t)}`);
    }
  }

  return parts.join(" • ");
};

/**
 * Get car license plate
 * @param car - Car object
 * @returns License plate string or null
 */
export const getCarLicensePlate = (car: Car | null | undefined): string | null => {
  if (!car) return null;
  return car.licensePlate || null;
};

/**
 * Check if car has an image
 * @param car - Car object
 * @returns True if car has an image URL
 */
export const hasCarImage = (car: Car | null | undefined): boolean => {
  if (!car) return false;
  return Boolean(car.uploadedFile?.fileUrl);
};

/**
 * Get car image URL
 * @param car - Car object
 * @returns Image URL or null
 */
export const getCarImageUrl = (car: Car | null | undefined): string | null => {
  if (!car) return null;
  return car.uploadedFile?.fileUrl || null;
};

/**
 * Get car thumbnail URL
 * @param car - Car object
 * @returns Thumbnail URL or null
 */
export const getCarThumbnailUrl = (car: Car | null | undefined): string | null => {
  if (!car) return null;
  return car.uploadedFile?.thumbnailUrl || car.uploadedFile?.fileUrl || null;
};

/**
 * Get drive type display name
 * @param driveType - Drive type code
 * @param t - Optional translation function
 * @returns Human-readable drive type name
 */
export const getDriveTypeDisplayName = (
  driveType: string | null | undefined,
  t?: (key: string) => string
): string => {
  if (!driveType) return "";

  const option = getDriveTypeOption(driveType);
  if (option && t) {
    return t(`common:${option.labelShortKey}`);
  }

  // Fallback: uppercase the value
  return driveType.toUpperCase();
};

/**
 * Get volume unit display name
 * @param unit - Volume unit code
 * @param t - Optional translation function
 * @returns Human-readable unit name
 */
export const getVolumeUnitDisplayName = (
  unit: string | null | undefined,
  t?: (key: string) => string
): string => {
  if (!unit) return "";

  const option = getTankVolumeUnitOption(unit);
  if (option && t) {
    return t(`common:${option.labelShortKey}`);
  }

  // Fallback display names
  const fallbacks: Record<string, string> = {
    l: "L",
    "gal-us": "gal",
    "gal-uk": "gal",
    kwh: "kWh",
  };

  return fallbacks[unit.toLowerCase()] || unit;
};